import { memo, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Maximize2 } from 'lucide-react';
import { ProtractorSvg, toRad, toDeg, polarPoint, normDeg, snapTo } from './SharedProtractor';
import { useQuizTheme, answerTheme, marqueSigne } from './quizTheme';
import PleinEcranInteractif from './PleinEcranInteractif';
import HtmlKatex from '../HtmlKatex';
import { etatsConstruction } from './corrige';
import ProgrammeConstructionRenderer from '../renderers/ProgrammeConstructionRenderer';

/**
 * Programme de construction interactif : l'élève suit des consignes pour tracer des cercles.
 * - Clic sur une consigne → active la tâche
 * - Clic sur un point nommé du SVG (snap < 20px) → pose le centre
 * - Drag pour ajuster le rayon → prévisualisation en pointillés
 * - Relâcher : si tolérance OK, cercle validé en couleur. Sinon feedback + retry
 * - Après 2 échecs sur une task, bouton "Passer la tâche" apparaît
 *
 * Props :
 * @param {Object} viewBox - { w, h }
 * @param {Array<{name, x, y}>} points - points nommés visibles sur le SVG
 * @param {Array<{type, from, to}>} preShapes - segments prédessinés
 * @param {Array<{id, consigne, color}>} tasks - consignes à réaliser
 * @param {number} tolerance - px tolérance pour centre ET rayon
 * @param {number} pxPerCm - facteur de conversion px → cm (pour affichage)
 * @param {string} bonneReponse - JSON string "{taskId: {cx,cy,r}}"
 * @param {string} selectedValue - JSON string réponse existante (restauration)
 * @param {Function} onAnswer - (jsonString) =>
 * @param {Function} onComplete - (bool) =>
 */
const ProgrammeConstructionEleve = memo(function ProgrammeConstructionEleve({
    viewBox, points = [], preShapes = [], tasks = [],
    tolerance = 12, pxPerCm = 30, unite = 'cm',
    bonneReponse, selectedValue, onAnswer, onComplete, onAbandon, corrige = null,
}) {
    const clair = useQuizTheme() === 'clair';
    const th = answerTheme(clair);
    const svgRef = useRef(null);

    // ─── Correction sur place (lot 6, 2026-09-21) ───
    // `corrige` n'arrive que si la réponse est fausse (contrat dans corrige.jsx).
    // Chaque tracé est validé au relâcher contre la géométrie attendue : un
    // tracé enregistré est juste, une consigne absente a été passée (deux
    // échecs) ou abandonnée. La correction : sa figure figée, les consignes
    // marquées d'une coche ou d'une croix, et À CÔTÉ la figure attendue
    // complète (« La réponse »), rendue par le composant qui existe déjà pour
    // ça. Boutons masqués, plus de plein écran. Sans `corrige`, le rendu d'avant.
    const etats = useMemo(() => (corrige ? etatsConstruction(corrige, tasks) : null), [corrige, tasks]);
    const [isExpanded, setIsExpanded] = useState(false); // mode plein écran (confort tactile mobile)
    const expected = useMemo(() => {
        try { return JSON.parse(bonneReponse || '{}'); } catch { return {}; }
    }, [bonneReponse]);

    // Réponses validées : { taskId: shape } où shape est :
    //  - cercle : { cx, cy, r }  (pas de champ type : backward-compat historique)
    //  - ligne  : { type: 'line', p1, p2 }
    //  - ray    : { type: 'ray', angleDeg }
    const [validatedShapes, setValidatedShapes] = useState(() => {
        if (selectedValue) {
            try {
                const parsed = JSON.parse(selectedValue);
                const shapes = {};
                Object.entries(parsed).forEach(([id, v]) => {
                    if (!v || typeof v !== 'object') return;
                    if (v.cx !== undefined) shapes[id] = v;                  // cercle
                    else if (v.type === 'line' && v.p1 && v.p2) shapes[id] = v;
                    else if (v.type === 'ray' && v.angleDeg !== undefined) shapes[id] = v;
                });
                return shapes;
            } catch { return {}; }
        }
        return {};
    });

    const [skippedTasks, setSkippedTasks] = useState(() => new Set());
    // Confirmation inline avant abandon (le travail en cours est effacé)
    const [confirmAbandon, setConfirmAbandon] = useState(false);
    const [activeTaskId, setActiveTaskId] = useState(null);
    const [phase, setPhase] = useState('idle'); // 'idle' | 'dragging'
    const [draft, setDraft] = useState(null);   // {cx, cy, r}
    const [failCount, setFailCount] = useState({});
    const [feedback, setFeedback] = useState(null); // {type: 'error'|'success', msg}
    // Pour les tasks kind='line' : mémorise le 1er point cliqué en attendant le 2e.
    const [firstClick, setFirstClick] = useState(null);

    // Pour les tasks kind='angle_ray' : position + rotation du rapporteur.
    const [protPos, setProtPos] = useState(null);       // { x, y }
    const [protRotation, setProtRotation] = useState(0); // deg
    const [protGesture, setProtGesture] = useState(null); // null | 'drag' | 'rotate' | 'aim'
    const [protGestureOffset, setProtGestureOffset] = useState({ x: 0, y: 0 });
    const [protPointerMoved, setProtPointerMoved] = useState(false);
    const [aimAngle, setAimAngle] = useState(null); // degré relatif 0–180 pendant la visée (glisser sur l'arc)

    // Reset du 1er clic si on change de task (ex: clic sur une autre consigne)
    useEffect(() => { setFirstClick(null); }, [activeTaskId]);

    const { w: W = 600, h: H = 450 } = viewBox || {};

    // Plus grand en plein écran (visée + poignée), mais pas au point d'écraser
    // la figure : le viewBox porte une marge proportionnelle à ce rayon, donc
    // tout ce qu'on ajoute ici rétrécit d'autant le dessin de l'élève.
    const PROT_RADIUS = isExpanded ? 140 : 130;

    // Portée du rapporteur : du centre jusqu'au bord de la poignée de rotation.
    const PROT_PORTEE = PROT_RADIUS + 29;

    // Marge autour de la figure quand l'exercice fait manipuler un rapporteur.
    // Sans marge, le rapporteur centré sur un sommet sortait du viewBox et s'y
    // faisait rogner, la poignée devenant parfois inatteignable.
    // Marge MINIMALE, et non « portée entière » : chaque pixel de marge rétrécit
    // le dessin, et en plein écran c'est la HAUTEUR qui est rare — une marge
    // généreuse annulait purement et simplement le gain du bouton agrandir.
    // Elle est STATIQUE : la déduire de la position courante ferait sauter la
    // figure à chaque déplacement.
    const MARGE_RAPPORTEUR = useMemo(() => {
        if (!tasks.some(t => t.kind === 'angle_ray')) return 0;
        // Plancher : la zone de pose ne doit pas être vide sur une petite figure.
        let m = Math.max(PROT_PORTEE - W / 2, PROT_PORTEE - H / 2, 0);
        // Puis de quoi centrer le rapporteur sur chaque point nommé.
        points.forEach((p) => {
            m = Math.max(m, PROT_PORTEE - p.x, p.x - W + PROT_PORTEE,
                PROT_PORTEE - p.y, p.y - H + PROT_PORTEE);
        });
        return Math.ceil(m);
    }, [tasks, points, PROT_PORTEE, W, H]);

    const VB = useMemo(() => ({
        x: -MARGE_RAPPORTEUR,
        y: -MARGE_RAPPORTEUR,
        w: W + 2 * MARGE_RAPPORTEUR,
        h: H + 2 * MARGE_RAPPORTEUR,
    }), [W, H, MARGE_RAPPORTEUR]);

    // Zone où peut se poser le CENTRE du rapporteur : exactement celle où il
    // reste entier dans le viewBox. Elle contient tous les points nommés, par
    // construction de la marge ci-dessus.
    const PROT_BORNES = useMemo(() => ({
        minX: PROT_PORTEE - MARGE_RAPPORTEUR,
        maxX: W + MARGE_RAPPORTEUR - PROT_PORTEE,
        minY: PROT_PORTEE - MARGE_RAPPORTEUR,
        maxY: H + MARGE_RAPPORTEUR - PROT_PORTEE,
    }), [PROT_PORTEE, MARGE_RAPPORTEUR, W, H]);

    // Trouve la task active courante
    const activeTask = useMemo(
        () => tasks.find(t => t.id === activeTaskId) || null,
        [tasks, activeTaskId],
    );

    // Cible à signaler pendant la tâche active, quand elle porte `showTarget`.
    // Réservé aux PLACEMENTS DE POINT (rayon attendu nul) : ailleurs, la cible
    // est une forme entière et un halo n'aurait pas de sens.
    const cibleAMontrer = useMemo(() => {
        if (!activeTask || !activeTask.showTarget) return null;
        const exp = expected[activeTask.id];
        if (!exp || exp.type || exp.r !== 0) return null;
        if (validatedShapes[activeTask.id]) return null;
        return { x: exp.cx, y: exp.cy };
    }, [activeTask, expected, validatedShapes]);

    // Infos de la task `angle_ray` active : vertex, ray1 de référence, angle cible.
    const angleRayInfo = useMemo(() => {
        if (!activeTask || activeTask.kind !== 'angle_ray') return null;
        const exp = expected[activeTask.id];
        if (!exp || exp.type !== 'ray') return null;
        const vertex = points.find(p => p.name === exp.from);
        if (!vertex) return null;
        // TOUS les côtés déjà tracés depuis le sommet sont des références
        // valables : sur une feuille, on pose la base du rapporteur indifféremment
        // sur l'un ou sur l'autre côté de l'angle. N'en retenir qu'un bloquait
        // l'élève en « oriente la base » pour une pose pourtant correcte.
        const refs = [];
        const dir = (de, vers) => toDeg(Math.atan2(-(vers.y - de.y), vers.x - de.x));
        const resoudre = (v) => (Array.isArray(v)
            ? { x: v[0], y: v[1], name: null }
            : points.find(p => p.name === v));
        preShapes.forEach((s) => {
            if (s.type === 'ray' && s.from === exp.from) {
                refs.push({ angle: s.angleDeg, labelEnd: s.labelEnd || null });
                return;
            }
            // Un SEGMENT déjà tracé qui touche le sommet est lui aussi un côté de
            // référence : la direction vers son autre extrémité en donne l'angle.
            // Sans cela, une figure dont la base est une `line` (les constructions
            // de triangle) retombait sur la valeur de repli 0°. Au sommet de
            // gauche c'était juste par hasard ; au sommet de DROITE, dont le côté
            // pointe vers 180°, la graduation annoncée était le supplément de
            // celle de la consigne.
            if (s.type !== 'line') return;
            const p1 = resoudre(s.from);
            const p2 = resoudre(s.to);
            if (!p1 || !p2) return;
            if (p1.name === exp.from) refs.push({ angle: dir(p1, p2), labelEnd: p2.name });
            else if (p2.name === exp.from) refs.push({ angle: dir(p2, p1), labelEnd: p1.name });
        });
        if (refs.length === 0) refs.push({ angle: 0, labelEnd: null });
        return { vertex, refs, targetAbs: exp.angleDeg };
    }, [activeTask, expected, points, preShapes]);

    // Init du rapporteur quand une task angle_ray devient active.
    // Position initiale décalée (haut-centre du viewBox) et rotation aléatoire
    // → l'élève doit réellement PLACER puis ORIENTER puis CLIQUER (3 étapes du
    // geste physique au rapporteur). Pas d'auto-centrage/auto-alignement.
    useEffect(() => {
        if (angleRayInfo) {
            // Position de départ : à distance notable du sommet, pour forcer le
            // drag, et DANS la zone de pose — sinon le premier déplacement
            // ramènerait brutalement le rapporteur à l'intérieur.
            const depart = {
                x: Math.max(PROT_BORNES.minX, Math.min(PROT_BORNES.maxX, W / 2)),
                y: Math.max(PROT_BORNES.minY, Math.min(PROT_BORNES.maxY, Math.max(60, H / 4))),
            };
            if (Math.hypot(depart.x - angleRayInfo.vertex.x, depart.y - angleRayInfo.vertex.y) < 40) {
                const milieu = (PROT_BORNES.minX + PROT_BORNES.maxX) / 2;
                depart.x = depart.x < milieu ? PROT_BORNES.maxX : PROT_BORNES.minX;
            }
            setProtPos(depart);
            // Rotation aléatoire entière (snap au degré → match exact atteignable).
            setProtRotation(Math.floor(Math.random() * 360));
        } else {
            setProtPos(null);
            setProtRotation(0);
        }
        setProtGesture(null);
        setProtPointerMoved(false);
    }, [activeTaskId, angleRayInfo, W, H, PROT_BORNES]);

    // Sur quel côté la base est-elle posée, et dans quel sens ?
    // Quatre poses sont correctes pour un angle à deux côtés : le 0 ou le 180 de
    // la base, sur l'un ou l'autre côté. `inverse` retient les poses où c'est le
    // 180 qui est aligné : l'élève lit alors l'échelle intérieure, soit
    // 180 moins la graduation extérieure.
    const alignement = useMemo(() => {
        if (!angleRayInfo) return null;
        const ecart = (ref) => Math.min(
            Math.abs(normDeg(protRotation - ref)),
            Math.abs(normDeg(protRotation - ref) - 360),
        );
        let best = null;
        angleRayInfo.refs.forEach((r) => {
            [[r.angle, false], [r.angle + 180, true]].forEach(([pose, inverse]) => {
                const e = ecart(pose);
                if (!best || e < best.ecart) best = { ecart: e, inverse, ref: r };
            });
        });
        return best && best.ecart < 2 ? best : null;
    }, [angleRayInfo, protRotation]);

    const protInverse = alignement ? alignement.inverse : false;

    // La cible, exprimée DEPUIS LA POSE ACTUELLE : `graduation` est le trait à
    // marquer sur le rapporteur (échelle extérieure, celle des traits), `mesure`
    // le nombre que l'élève lit dessus. `null` quand la cible tombe sous la
    // base : aucune graduation ne la porte, c'est le rapporteur qu'il faut
    // retourner.
    const cibleLecture = useMemo(() => {
        if (!angleRayInfo) return null;
        const graduation = normDeg(angleRayInfo.targetAbs - protRotation);
        if (graduation > 180) return null;
        return {
            graduation: Math.round(graduation),
            mesure: Math.round(protInverse ? 180 - graduation : graduation),
        };
    }, [angleRayInfo, protRotation, protInverse]);

    // « [JQ) » ou « [JQ) ou [JZ) » selon le nombre de côtés déjà tracés.
    const refsLabel = useMemo(() => {
        if (!angleRayInfo) return '';
        const v = angleRayInfo.vertex.name;
        return angleRayInfo.refs
            .filter(r => r.labelEnd)
            .map(r => `[${v}${r.labelEnd})`)
            .join(' ou ');
    }, [angleRayInfo]);

    // Détection automatique des phases du rapporteur
    const protPhase = useMemo(() => {
        if (!angleRayInfo || !protPos) return null;
        const dVertex = Math.hypot(protPos.x - angleRayInfo.vertex.x, protPos.y - angleRayInfo.vertex.y);
        if (dVertex >= 10) return 'placing';
        return alignement ? 'ready' : 'orienting';
    }, [angleRayInfo, protPos, alignement]);

    // Initialiser activeTask à la première non traitée
    useEffect(() => {
        if (activeTaskId) return;
        const firstPending = tasks.find(t => !validatedShapes[t.id] && !skippedTasks.has(t.id));
        if (firstPending) setActiveTaskId(firstPending.id);
    }, [tasks, activeTaskId, validatedShapes, skippedTasks]);

    // ─── Émettre la réponse + signaler la complétion ───
    const emitAnswer = useCallback((shapes, skipped) => {
        onAnswer(JSON.stringify(shapes));
        const total = tasks.length;
        // Compter sur les ids de TÂCHES uniquement : `shapes` contient aussi les
        // clés `__extra` (segments auto-tracés) — les compter faisait dépasser
        // `total` et onComplete(true) n'arrivait jamais (Suivant invisible).
        const done = tasks.filter(t => shapes[t.id]).length + skipped.size;
        onComplete(total > 0 && done === total);
    }, [tasks, onAnswer, onComplete]);

    // ─── Abandonner l'exercice : réponse vide (comptée fausse) + avance ───
    const handleAbandon = useCallback(() => {
        onAnswer(JSON.stringify({}));
        onComplete(true);
        if (onAbandon) onAbandon();
    }, [onAnswer, onComplete, onAbandon]);

    // ─── Conversion position pointer → coords SVG (consciente du letterbox `meet`) ───
    // En plein écran le SVG est en width/height:100% → la figure est centrée avec des
    // marges (preserveAspectRatio meet) ; il faut donc retirer l'offset et diviser par
    // l'échelle réelle, sinon les hitbox sont décalées. En inline (w-full, hauteur auto)
    // les offsets valent 0 → formule identique à l'ancienne.
    const svgCoords = useCallback((e) => {
        const svg = svgRef.current;
        if (!svg) return { x: 0, y: 0 };
        const rect = svg.getBoundingClientRect();
        if (!rect.width || !rect.height) return { x: 0, y: 0 };
        // Doit refléter EXACTEMENT le viewBox rendu (marge rapporteur comprise),
        // sinon le pointeur et la figure se décalent.
        const scale = Math.min(rect.width / VB.w, rect.height / VB.h);
        const offsetX = (rect.width - VB.w * scale) / 2;
        const offsetY = (rect.height - VB.h * scale) / 2;
        return {
            x: (e.clientX - rect.left - offsetX) / scale + VB.x,
            y: (e.clientY - rect.top - offsetY) / scale + VB.y,
        };
    }, [VB]);

    // Trouve le point nommé le plus proche (snap)
    const findNearestPoint = useCallback((x, y, radius = 20) => {
        let best = null, bestDist = radius;
        points.forEach(p => {
            const d = Math.hypot(p.x - x, p.y - y);
            if (d < bestDist) {
                best = p;
                bestDist = d;
            }
        });
        return best;
    }, [points]);

    // Trouve le target (parmi une liste arbitraire {x,y}) le plus proche d'un clic.
    const findNearestTarget = useCallback((x, y, targets, radius) => {
        let best = null, bestDist = radius;
        targets.forEach(t => {
            const d = Math.hypot(t.x - x, t.y - y);
            if (d < bestDist) { best = t; bestDist = d; }
        });
        return best;
    }, []);

    // Étend une droite (définie par 2 points) aux bords du viewBox.
    // Utilisé pour rendre les droites validées (kind='line') sur toute la zone.
    const extendLineToViewBox = useCallback((p1, p2) => {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        const candidates = [];
        if (Math.abs(dx) > 1e-6) {
            candidates.push({ t: (0 - p1.x) / dx });
            candidates.push({ t: (W - p1.x) / dx });
        }
        if (Math.abs(dy) > 1e-6) {
            candidates.push({ t: (0 - p1.y) / dy });
            candidates.push({ t: (H - p1.y) / dy });
        }
        const pts = candidates
            .map(({ t }) => ({ x: p1.x + t * dx, y: p1.y + t * dy, t }))
            .filter(p => p.x >= -0.01 && p.x <= W + 0.01 && p.y >= -0.01 && p.y <= H + 0.01);
        if (pts.length < 2) return { x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y };
        pts.sort((a, b) => a.t - b.t);
        return { x1: pts[0].x, y1: pts[0].y, x2: pts[pts.length - 1].x, y2: pts[pts.length - 1].y };
    }, [W, H]);

    // ─── Interactions pointeur : rapporteur (task angle_ray) ───
    // PROT_RADIUS est déclaré plus haut : la marge du viewBox en dépend.

    // Position de la poignée de rotation (en coords SVG globales)
    const protRotHandlePos = useMemo(() => {
        if (!protPos) return null;
        return {
            x: protPos.x + (PROT_RADIUS + 16) * Math.cos(toRad(-protRotation)),
            y: protPos.y + (PROT_RADIUS + 16) * Math.sin(toRad(-protRotation)),
        };
    }, [protPos, protRotation, PROT_RADIUS]);

    const onProtBodyPointerDown = useCallback((e) => {
        if (!protPos) return;
        e.stopPropagation();
        const pt = svgCoords(e);
        const d = Math.hypot(pt.x - protPos.x, pt.y - protPos.y);
        // Rapporteur prêt + appui sur l'arc → VISÉE (glisser pour choisir l'angle).
        // Sinon → déplacement du rapporteur (comportement habituel).
        if (protPhase === 'ready' && d >= PROT_RADIUS * 0.3 && d <= PROT_RADIUS + 12) {
            setProtGesture('aim');
            setProtPointerMoved(false);
            const rel = normDeg(toDeg(Math.atan2(-(pt.y - protPos.y), pt.x - protPos.x)) - protRotation);
            if (rel >= 0 && rel <= 180) setAimAngle(Math.round(protInverse ? 180 - rel : rel));
            try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
            return;
        }
        setProtGesture('drag');
        setProtPointerMoved(false);
        setProtGestureOffset({ x: pt.x - protPos.x, y: pt.y - protPos.y });
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
    }, [protPos, protPhase, protRotation, protInverse, PROT_RADIUS, svgCoords]);

    const onProtRotateHandlePointerDown = useCallback((e) => {
        e.stopPropagation();
        setProtGesture('rotate');
        setProtPointerMoved(false);
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
    }, []);

    const onProtPointerMove = useCallback((e) => {
        if (!protGesture || !protPos) return;
        const pt = svgCoords(e);
        if (protGesture === 'drag') {
            // Le centre reste dans la zone de pose : le rapporteur est alors
            // TOUJOURS visible en entier, poignée de rotation comprise.
            const newX = Math.max(PROT_BORNES.minX, Math.min(PROT_BORNES.maxX, pt.x - protGestureOffset.x));
            const newY = Math.max(PROT_BORNES.minY, Math.min(PROT_BORNES.maxY, pt.y - protGestureOffset.y));
            if (Math.hypot(newX - protPos.x, newY - protPos.y) > 2) setProtPointerMoved(true);
            setProtPos({ x: newX, y: newY });
        } else if (protGesture === 'rotate') {
            const a = toDeg(Math.atan2(-(pt.y - protPos.y), pt.x - protPos.x));
            const snapped = snapTo(normDeg(a), 1);
            if (Math.abs(snapped - protRotation) > 0.5) setProtPointerMoved(true);
            setProtRotation(snapped);
        } else if (protGesture === 'aim') {
            const rel = normDeg(toDeg(Math.atan2(-(pt.y - protPos.y), pt.x - protPos.x)) - protRotation);
            if (rel >= 0 && rel <= 180) setAimAngle(Math.round(protInverse ? 180 - rel : rel));
            setProtPointerMoved(true);
        }
    }, [protGesture, protPos, protGestureOffset, protRotation, protInverse, svgCoords, PROT_BORNES]);

    // Valide la visée courante — réutilisé par le tap ET le glisser-viser.
    // `snapped` est ce que l'élève LIT sur l'échelle qu'il a alignée. Ce qu'on
    // valide est la DIRECTION ABSOLUE ainsi visée : c'est la seule grandeur
    // indépendante du côté sur lequel il a posé sa base et du sens de lecture.
    const validateAngle = useCallback((snapped) => {
        if (snapped == null || !angleRayInfo || !activeTask) return;
        const graduation = protInverse ? 180 - snapped : snapped;
        const absAngle = normDeg(protRotation + graduation);
        const brut = normDeg(absAngle - angleRayInfo.targetAbs);
        const delta = Math.min(brut, 360 - brut);
        const tolDeg = activeTask.tolerance_deg ?? 2;
        if (delta <= tolDeg) {
            const newShape = { type: 'ray', angleDeg: absAngle };
            const next = { ...validatedShapes, [activeTask.id]: newShape };
            setValidatedShapes(next);
            setFeedback({ type: 'success', taskId: activeTask.id, msg: 'Angle tracé !' });
            setTimeout(() => setFeedback(null), 1500);
            emitAnswer(next, skippedTasks);
            const nextPending = tasks.find(t => !next[t.id] && !skippedTasks.has(t.id) && t.id !== activeTask.id);
            setActiveTaskId(nextPending ? nextPending.id : null);
        } else {
            // Ce que l'élève aurait dû lire DEPUIS SA POSE : lui renvoyer une
            // mesure comptée depuis un autre côté que le sien ne lui servirait
            // à rien.
            setFeedback({
                type: 'error',
                taskId: activeTask.id,
                msg: cibleLecture === null
                    ? 'La demi-droite à tracer passe sous la base du rapporteur : retourne-le.'
                    : `Tu as cliqué ${snapped}° — attendu ${cibleLecture.mesure}°`,
            });
            setTimeout(() => setFeedback(null), 2500);
            setFailCount(prev => ({ ...prev, [activeTask.id]: (prev[activeTask.id] || 0) + 1 }));
        }
    }, [angleRayInfo, activeTask, validatedShapes, skippedTasks, tasks, emitAnswer, protRotation, protInverse, cibleLecture]);

    const onProtPointerUp = useCallback((e) => {
        // Fin de visée (glisser ou simple tap sur l'arc quand prêt) → valider l'angle courant.
        if (protGesture === 'aim') {
            validateAngle(aimAngle);
            setAimAngle(null);
            setProtGesture(null);
            setProtPointerMoved(false);
            return;
        }
        // Tap sur l'arc alors que le rapporteur n'est pas encore prêt → rappel pédagogique.
        if (protGesture === 'drag' && !protPointerMoved && angleRayInfo && activeTask && protPos) {
            const pt = svgCoords(e);
            const d = Math.hypot(pt.x - protPos.x, pt.y - protPos.y);
            const clickOnArc = d >= PROT_RADIUS * 0.3 && d <= PROT_RADIUS + 10;
            if (clickOnArc && protPhase !== 'ready') {
                const hint = protPhase === 'placing'
                    ? `Avant de cliquer, centre d'abord le rapporteur sur ${angleRayInfo.vertex.name}.`
                    : `Avant de cliquer, aligne la base du rapporteur sur ${refsLabel || 'un côté de l\'angle'}.`;
                setFeedback({ type: 'error', taskId: activeTask.id, msg: hint });
                setTimeout(() => setFeedback(null), 2500);
            }
        }
        setProtGesture(null);
        setProtPointerMoved(false);
    }, [protGesture, aimAngle, validateAngle, protPointerMoved, angleRayInfo, activeTask, protPos, protPhase, PROT_RADIUS, svgCoords]);

    // Étend un rayon à partir de `from` dans la direction `angleDeg` jusqu'aux bords du viewBox.
    const extendRayToViewBox = useCallback((from, angleDeg) => {
        const dx = Math.cos(toRad(-angleDeg));
        const dy = Math.sin(toRad(-angleDeg));
        // On part de `from` et on avance dans la direction (dx,dy). On cherche le t > 0 qui
        // nous fait sortir du viewBox.
        const ts = [];
        if (Math.abs(dx) > 1e-6) {
            const t1 = (0 - from.x) / dx;
            const t2 = (W - from.x) / dx;
            if (t1 > 0) ts.push(t1);
            if (t2 > 0) ts.push(t2);
        }
        if (Math.abs(dy) > 1e-6) {
            const t1 = (0 - from.y) / dy;
            const t2 = (H - from.y) / dy;
            if (t1 > 0) ts.push(t1);
            if (t2 > 0) ts.push(t2);
        }
        const tMax = ts.length ? Math.min(...ts) : 200;
        return { x: from.x + tMax * dx, y: from.y + tMax * dy };
    }, [W, H]);

    // ─── Interactions pointeur ───
    const onPointerDown = useCallback((e) => {
        if (!activeTask) return;
        const { x, y } = svgCoords(e);
        const activeKind = activeTask.kind || 'circle';
        const exp = expected[activeTask.id];

        // Mode 'angle_ray' : tout est géré par les handlers spécifiques du rapporteur
        // (onProtBodyPointerDown sur le corps, onProtRotateHandlePointerDown sur la poignée).
        // Si le clic arrive ici c'est qu'il a tapé hors-rapporteur → on ignore.
        if (activeKind === 'angle_ray') return;

        // Mode 'line' ou 'segment' : 2 clics successifs, chacun snap strict sur un
        // endpoint attendu. 'line' → droite infinie ; 'segment' → segment borné.
        const isLineOrSegment = (activeKind === 'line' || activeKind === 'segment')
            && exp && (exp.type === 'line' || exp.type === 'segment');
        if (isLineOrSegment) {
            const remaining = [exp.p1, exp.p2].filter(p => {
                if (!firstClick) return true;
                return !(Math.abs(p.x - firstClick.x) < 0.1 && Math.abs(p.y - firstClick.y) < 0.1);
            });
            const snap = findNearestTarget(x, y, remaining, tolerance + 4);
            if (!snap) {
                const errMsg = activeTask.pointsOnly
                    ? 'Clique sur un des deux points demandés'
                    : (activeKind === 'segment'
                        ? "Clique sur une extrémité du segment à tracer"
                        : "Clique sur un point d'intersection des deux arcs");
                setFeedback({ type: 'error', taskId: activeTask.id, msg: errMsg });
                setTimeout(() => setFeedback(null), 2500);
                setFailCount(prev => ({ ...prev, [activeTask.id]: (prev[activeTask.id] || 0) + 1 }));
                return;
            }
            if (!firstClick) {
                setFirstClick({ x: snap.x, y: snap.y });
                return;
            }
            // 2e clic : valide la forme
            const shapeType = activeKind === 'segment' ? 'segment' : 'line';
            const newShape = { type: shapeType, p1: firstClick, p2: { x: snap.x, y: snap.y } };
            const extras = Object.fromEntries(
                (activeTask.extraShapes || []).map((s, i) => [`${activeTask.id}__extra${i}`, s]),
            );
            const next = { ...validatedShapes, [activeTask.id]: newShape, ...extras };
            setValidatedShapes(next);
            setFirstClick(null);
            // `pointsOnly` : la tâche fait cliquer deux points sans tracer le trait
            // qui les joint — annoncer « Segment tracé ! » nommerait un objet qui
            // n'existe pas et contredirait ce que l'élève voit à l'écran.
            const okMsg = activeTask.pointsOnly
                ? 'Bien !'
                : (activeKind === 'segment' ? 'Segment tracé !' : 'Médiatrice tracée !');
            setFeedback({ type: 'success', taskId: activeTask.id, msg: okMsg });
            setTimeout(() => setFeedback(null), 1500);
            emitAnswer(next, skippedTasks);
            const nextPending = tasks.find(t => !next[t.id] && !skippedTasks.has(t.id) && t.id !== activeTask.id);
            setActiveTaskId(nextPending ? nextPending.id : null);
            return;
        }

        // Mode 'circle' (défaut historique) : centre snappé + drag pour le rayon
        const snapped = findNearestPoint(x, y, 22);
        if (!snapped) {
            // Cas milieu : on autorise aussi le clic libre — la validation comparera au centre attendu
            if (!exp) return;
            // Si l'élève clique proche du centre attendu (même si non-point nommé)
            if (Math.hypot(x - exp.cx, y - exp.cy) < tolerance + 6) {
                setDraft({ cx: exp.cx, cy: exp.cy, r: 0 });
                setPhase('dragging');
                try { svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
            }
            return;
        }
        setDraft({ cx: snapped.x, cy: snapped.y, r: 0 });
        setPhase('dragging');
        try { svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
    }, [activeTask, svgCoords, findNearestPoint, findNearestTarget, expected, tolerance, firstClick, validatedShapes, skippedTasks, tasks, emitAnswer]);

    const onPointerMove = useCallback((e) => {
        // Si on est en train de manipuler le rapporteur → déléguer
        if (protGesture) { onProtPointerMove(e); return; }
        if (phase !== 'dragging' || !draft) return;
        const { x, y } = svgCoords(e);
        const r = Math.hypot(x - draft.cx, y - draft.cy);
        setDraft(d => (d ? { ...d, r } : null));
    }, [protGesture, onProtPointerMove, phase, draft, svgCoords]);

    const onPointerUp = useCallback((e) => {
        // Si on est en train de manipuler le rapporteur → déléguer (gère aussi le click-graduation)
        if (protGesture) { onProtPointerUp(e); return; }
        if (phase !== 'dragging' || !draft || !activeTask) {
            setPhase('idle');
            setDraft(null);
            return;
        }
        const exp = expected[activeTask.id];
        const centerOk = exp && Math.hypot(draft.cx - exp.cx, draft.cy - exp.cy) < tolerance;
        const radiusOk = exp && Math.abs(draft.r - exp.r) < tolerance;

        if (centerOk && radiusOk) {
            // Snap-on-success : la tolérance juge l'INTENTION du geste, mais on
            // affiche la géométrie EXACTE attendue. Sinon les arcs gardent le
            // rayon approximatif du geste (ex. 82.5 px pour 90 attendus) et leur
            // intersection visuelle ne passe plus par le point cible — la
            // construction validée paraît fausse. Même esprit que le snap strict
            // des extrémités en mode line/segment.
            const finalShape = { cx: exp.cx, cy: exp.cy, r: exp.r };
            const extras = Object.fromEntries(
                (activeTask.extraShapes || []).map((s, i) => [`${activeTask.id}__extra${i}`, s]),
            );
            const next = { ...validatedShapes, [activeTask.id]: finalShape, ...extras };
            setValidatedShapes(next);
            setFeedback({ type: 'success', taskId: activeTask.id, msg: 'Bien !' });
            setTimeout(() => setFeedback(null), 1500);
            emitAnswer(next, skippedTasks);
            // Sélectionne la prochaine task non traitée
            const nextPending = tasks.find(t => !next[t.id] && !skippedTasks.has(t.id) && t.id !== activeTask.id);
            setActiveTaskId(nextPending ? nextPending.id : null);
        } else {
            const msg = !centerOk
                ? "Ce n'est pas le bon centre"
                : draft.r > exp.r
                    ? 'Rayon trop grand'
                    : 'Rayon trop petit';
            setFeedback({ type: 'error', taskId: activeTask.id, msg });
            setTimeout(() => setFeedback(null), 2500);
            setFailCount(prev => ({ ...prev, [activeTask.id]: (prev[activeTask.id] || 0) + 1 }));
        }
        setPhase('idle');
        setDraft(null);
        try { svgRef.current?.releasePointerCapture?.(e.pointerId); } catch { /* no-op */ }
    }, [protGesture, onProtPointerUp, phase, draft, activeTask, expected, tolerance, validatedShapes, skippedTasks, tasks, emitAnswer]);

    // ─── Actions UI ───
    const handleTaskClick = useCallback((taskId) => {
        if (validatedShapes[taskId] || skippedTasks.has(taskId)) return;
        setActiveTaskId(taskId);
        setFeedback(null);
    }, [validatedShapes, skippedTasks]);

    const handleSkipTask = useCallback(() => {
        if (!activeTask) return;
        const nextSkipped = new Set(skippedTasks);
        nextSkipped.add(activeTask.id);
        setSkippedTasks(nextSkipped);
        emitAnswer(validatedShapes, nextSkipped);
        const nextPending = tasks.find(t => !validatedShapes[t.id] && !nextSkipped.has(t.id) && t.id !== activeTask.id);
        setActiveTaskId(nextPending ? nextPending.id : null);
        setFeedback(null);
    }, [activeTask, skippedTasks, validatedShapes, emitAnswer, tasks]);

    // ─── Rendu ───
    // Ids de tâches uniquement (les clés __extra ne comptent pas comme tracés)
    const nSolved = tasks.filter(t => validatedShapes[t.id]).length;
    const nSkipped = skippedTasks.size;
    const nTotal = tasks.length;
    const canSkip = activeTask && (failCount[activeTask.id] || 0) >= 2;

    // Instruction contextuelle
    const instruction = (() => {
        if (!activeTask) {
            return nSolved + nSkipped === nTotal
                ? '🎯 Toutes les consignes ont été traitées.'
                : 'Clique sur une consigne pour commencer.';
        }
        const activeKind = activeTask.kind || 'circle';
        // `pointsOnly` : deux points à toucher, aucun trait à tracer. Les messages
        // ne doivent nommer ni segment ni droite — l'élève ne verra que deux points.
        if (activeTask.pointsOnly) {
            if (feedback?.type === 'error') return `${feedback.msg} — recommence.`;
            if (!firstClick) return 'Clique sur le premier point.';
            return 'Clique maintenant sur le second point.';
        }
        if (activeKind === 'line') {
            if (feedback?.type === 'error') return `${feedback.msg} — recommence.`;
            if (!firstClick) return "Clique sur le premier point d'intersection des deux arcs.";
            return "Clique maintenant sur le deuxième point d'intersection.";
        }
        if (activeKind === 'angle_ray' && angleRayInfo) {
            if (feedback?.type === 'error') return `${feedback.msg} — recommence.`;
            const vertexName = angleRayInfo.vertex.name;
            if (protPhase === 'placing') return `Étape 1 — Centre le rapporteur sur le sommet ${vertexName}.`;
            if (protPhase === 'orienting') return `Étape 2 — Aligne la base du rapporteur sur ${refsLabel || 'un côté de l\'angle'}.`;
            // La graduation annoncée est celle que porte l'échelle alignée : elle
            // change si l'élève retourne le rapporteur ou change de côté.
            if (protPhase === 'ready') {
                return cibleLecture
                    ? `Étape 3 — Clique sur la graduation ${cibleLecture.mesure}° pour tracer le côté.`
                    : 'Étape 3 — Retourne le rapporteur : le côté à tracer passe sous sa base.';
            }
        }
        if (phase === 'dragging') return 'Relâche pour valider le rayon.';
        if (feedback?.type === 'error') return `${feedback.msg} — recommence.`;
        return 'Clique sur le centre dans la figure, puis glisse pour le rayon.';
    })();

    // Le canevas de la figure (sa construction) : seul, ou à côté de la figure attendue en correction
    const canvas = (
            <div
                className={`${isExpanded ? 'flex-1 min-h-0 flex items-center justify-center overflow-hidden ' : ''}w-full rounded-xl border-2 transition-all bg-white ${
                    activeTask && !corrige ? 'border-blue-500/50 cursor-crosshair' : (clair ? 'border-slate-300' : 'border-slate-600')
                }`}
            >
                <svg
                    ref={svgRef}
                    viewBox={`${VB.x} ${VB.y} ${VB.w} ${VB.h}`}
                    className={isExpanded ? '' : 'w-full'}
                    preserveAspectRatio="xMidYMid meet"
                    onPointerDown={corrige ? undefined : onPointerDown}
                    onPointerMove={corrige ? undefined : onPointerMove}
                    onPointerUp={corrige ? undefined : onPointerUp}
                    onPointerCancel={corrige ? undefined : onPointerUp}
                    style={isExpanded ? { touchAction: 'none', width: '100%', height: '100%' } : { touchAction: 'none' }}
                >
                    {/* Grille de fond */}
                    <defs>
                        <pattern id="pc-grid" width="30" height="30" patternUnits="userSpaceOnUse">
                            <path d="M 30 0 L 0 0 0 30" fill="none" stroke="#e0e0da" strokeWidth="0.4" />
                        </pattern>
                    </defs>
                    <rect width={W} height={H} fill="url(#pc-grid)" />

                    {/* Segments prédessinés (type 'line') + demi-droites (type 'ray') */}
                    {preShapes.map((s, i) => {
                        if (s.type === 'line') {
                            const from = Array.isArray(s.from) ? { x: s.from[0], y: s.from[1] } : points.find(p => p.name === s.from);
                            const to = Array.isArray(s.to) ? { x: s.to[0], y: s.to[1] } : points.find(p => p.name === s.to);
                            if (!from || !to) return null;
                            return (
                                <line
                                    key={`preshape-${i}`}
                                    x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                                    stroke="#2c2c2a" strokeWidth="1.5" strokeLinecap="round"
                                />
                            );
                        }
                        if (s.type === 'ray') {
                            const from = points.find(p => p.name === s.from);
                            if (!from) return null;
                            const length = s.length ?? 220;
                            const end = polarPoint(from.x, from.y, length, s.angleDeg);
                            // Flèche de fin
                            const dx = end.x - from.x, dy = end.y - from.y;
                            const len = Math.hypot(dx, dy) || 1;
                            const ux = dx / len, uy = dy / len;
                            const tipLen = 10;
                            const tipPts = `${end.x},${end.y} ${end.x - tipLen * ux + tipLen * 0.35 * uy},${end.y - tipLen * uy - tipLen * 0.35 * ux} ${end.x - tipLen * ux - tipLen * 0.35 * uy},${end.y - tipLen * uy + tipLen * 0.35 * ux}`;
                            return (
                                <g key={`preshape-${i}`}>
                                    <line x1={from.x} y1={from.y} x2={end.x} y2={end.y} stroke="#2c2c2a" strokeWidth="1.5" strokeLinecap="round" />
                                    <polygon points={tipPts} fill="#2c2c2a" />
                                    {s.labelEnd && (
                                        <text
                                            x={end.x + 6} y={end.y + 4}
                                            fontSize={13} fontWeight={600}
                                            fill="#2c2c2a" fontFamily="system-ui,sans-serif"
                                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                                        >{s.labelEnd}</text>
                                    )}
                                </g>
                            );
                        }
                        return null;
                    })}

                    {/* Formes validées (cercles ou droites/rays selon shape.type) */}
                    {tasks.map(t => {
                        const shape = validatedShapes[t.id];
                        if (!shape) return null;
                        if (shape.type === 'line' || shape.type === 'segment') {
                            // `pointsOnly` (opt-in, posé par le générateur sur la tâche) :
                            // la tâche sert à FAIRE CLIQUER deux points, pas à tracer le
                            // trait qui les joint. Cas d'usage : deux positions possibles
                            // à identifier (intersection de deux cercles) — un trait
                            // laisserait croire que la réponse court tout au long.
                            // Le mode 2 clics reste celui de line/segment, seul le rendu change.
                            const endpoints = shape.type === 'segment'
                                ? { x1: shape.p1.x, y1: shape.p1.y, x2: shape.p2.x, y2: shape.p2.y }
                                : extendLineToViewBox(shape.p1, shape.p2);
                            return (
                                <g key={`validated-${t.id}`}>
                                    {!t.pointsOnly && (
                                        <line x1={endpoints.x1} y1={endpoints.y1} x2={endpoints.x2} y2={endpoints.y2}
                                            stroke={t.color} strokeWidth="2.5" strokeLinecap="round" />
                                    )}
                                    <circle cx={shape.p1.x} cy={shape.p1.y} r={t.pointsOnly ? 5 : 3} fill={t.color} />
                                    <circle cx={shape.p2.x} cy={shape.p2.y} r={t.pointsOnly ? 5 : 3} fill={t.color} />
                                </g>
                            );
                        }
                        if (shape.type === 'ray') {
                            // Trouver le point from depuis expected (on n'a pas les points dans la shape validée)
                            const expRay = expected[t.id];
                            const from = points.find(p => p.name === expRay?.from);
                            if (!from) return null;
                            const end = extendRayToViewBox(from, shape.angleDeg);
                            const dx = end.x - from.x, dy = end.y - from.y;
                            const len = Math.hypot(dx, dy) || 1;
                            const ux = dx / len, uy = dy / len;
                            const tipLen = 10;
                            const tipPts = `${end.x},${end.y} ${end.x - tipLen * ux + tipLen * 0.35 * uy},${end.y - tipLen * uy - tipLen * 0.35 * ux} ${end.x - tipLen * ux - tipLen * 0.35 * uy},${end.y - tipLen * uy + tipLen * 0.35 * ux}`;
                            return (
                                <g key={`validated-${t.id}`}>
                                    <line x1={from.x} y1={from.y} x2={end.x} y2={end.y}
                                        stroke={t.color} strokeWidth="2.5" strokeLinecap="round" />
                                    <polygon points={tipPts} fill={t.color} />
                                    <circle cx={from.x} cy={from.y} r={3} fill={t.color} />
                                    {expRay?.labelEnd && (
                                        <text x={end.x + 6} y={end.y + 4} fontSize={13} fontWeight={600} fill={t.color} fontFamily="system-ui,sans-serif" style={{ pointerEvents: 'none', userSelect: 'none' }}>{expRay.labelEnd}</text>
                                    )}
                                </g>
                            );
                        }
                        // Idiome "placement de point" — détecté sur la SPEC (rayon
                        // ATTENDU nul), pas sur le geste : la validation tolère ±12px,
                        // donc le rayon stocké peut valoir quelques px si le pointeur a
                        // bougé pendant le clic. (shape.r < 1 gardé en filet pour les
                        // réponses déjà enregistrées sans expected.)
                        const expShape = expected[t.id];
                        const estPoint = (expShape && !expShape.type && expShape.r === 0) || shape.r < 1;
                        if (estPoint) {
                            return (
                                <g key={`validated-${t.id}`}>
                                    <circle cx={shape.cx} cy={shape.cy} r={4.5}
                                        fill={t.color} stroke="white" strokeWidth="1.5" />
                                    {t.pointLabel && (
                                        <text
                                            x={shape.cx + 8} y={shape.cy - 7}
                                            fontSize={14} fontWeight={600}
                                            fill={t.color}
                                            stroke="white" strokeWidth="3"
                                            paintOrder="stroke" strokeLinejoin="round"
                                            fontFamily="system-ui,sans-serif"
                                            style={{ pointerEvents: 'none', userSelect: 'none' }}
                                        >
                                            {t.pointLabel}
                                        </text>
                                    )}
                                </g>
                            );
                        }
                        // Cercle (pas de champ type : backward-compat)
                        return (
                            <g key={`validated-${t.id}`}>
                                <circle cx={shape.cx} cy={shape.cy} r={shape.r}
                                    fill="none" stroke={t.color} strokeWidth="2.5" />
                                <circle cx={shape.cx} cy={shape.cy} r={2.5} fill={t.color} />
                            </g>
                        );
                    })}

                    {/* Étiquette différée `labelOnDone` (opt-in, posée par le générateur
                        sur la tâche) : un texte {x, y, text} affiché UNIQUEMENT une fois
                        la tâche validée. Cas d'usage (gen_1023, report au compas) : la
                        mesure de l'écartement s'écrit sous le segment après la prise.
                        Purement additif — aucune tâche existante ne porte ce champ. */}
                    {tasks.map(t => {
                        if (!t.labelOnDone || !validatedShapes[t.id]) return null;
                        const l = t.labelOnDone;
                        return (
                            <text key={`labeldone-${t.id}`}
                                x={l.x} y={l.y}
                                fontSize={13} fontWeight={700}
                                fill={t.color || '#1D9E75'}
                                stroke="white" strokeWidth="3"
                                paintOrder="stroke" strokeLinejoin="round"
                                fontFamily="system-ui,sans-serif"
                                style={{ pointerEvents: 'none', userSelect: 'none' }}
                            >
                                {l.text}
                            </text>
                        );
                    })}

                    {/* Shapes bonus auto-tracées quand une task expose `extraShapes` (ex. fermer
                        automatiquement un triangle en SSS à la validation du dernier sommet). */}
                    {Object.entries(validatedShapes)
                        .filter(([k]) => k.includes('__extra'))
                        .map(([k, shape]) => {
                            const color = shape.color || '#1D9E75';
                            if (shape.type === 'segment' || shape.type === 'line') {
                                return (
                                    <line key={`extra-${k}`}
                                        x1={shape.p1.x} y1={shape.p1.y} x2={shape.p2.x} y2={shape.p2.y}
                                        stroke={color} strokeWidth="2.5" strokeLinecap="round" />
                                );
                            }
                            return null;
                        })}

                    {/* Repère de la cible, sur les tâches qui portent `showTarget`.
                        Cas d'usage : deux arcs se croisent en DEUX points et un seul
                        referme le triangle attendu ; le halo dit lequel viser, ce que
                        la consigne dit déjà par écrit. Opt-in au niveau de la TÂCHE :
                        activé globalement, il rendrait triviaux tous les placements de
                        point des autres générateurs. */}
                    {cibleAMontrer && (
                        <g style={{ pointerEvents: 'none' }}>
                            <circle cx={cibleAMontrer.x} cy={cibleAMontrer.y} r={14}
                                fill="#22c55e" opacity={0.22} />
                            <circle cx={cibleAMontrer.x} cy={cibleAMontrer.y} r={6}
                                fill="none" stroke="#1D9E75" strokeWidth={2} opacity={0.9} />
                        </g>
                    )}

                    {/* Rapporteur interactif pour task active kind='angle_ray' */}
                    {activeTask && activeTask.kind === 'angle_ray' && angleRayInfo && protPos && (() => {
                        // Le trait mis en évidence appartient à l'échelle EXTÉRIEURE
                        // (c'est l'index des ticks) : `graduation`, pas `mesure`.
                        const traitCible = cibleLecture ? cibleLecture.graduation : null;
                        return (
                            <g>
                                {/* Aide visuelle phase 'placing' : halo vert pulsant sur le sommet */}
                                {protPhase === 'placing' && (
                                    <circle
                                        cx={angleRayInfo.vertex.x} cy={angleRayInfo.vertex.y} r={14}
                                        fill="#22c55e" opacity={0.25}
                                    />
                                )}
                                {/* Corps du rapporteur — drag pour déplacer */}
                                <g
                                    onPointerDown={onProtBodyPointerDown}
                                    style={{ cursor: protGesture === 'drag' ? 'grabbing' : 'grab' }}
                                >
                                    <ProtractorSvg
                                        cx={protPos.x} cy={protPos.y}
                                        rotation={protRotation}
                                        radius={PROT_RADIUS}
                                        theme={clair ? 'light' : 'dark'}
                                        highlighted={protPhase === 'ready' ? traitCible : null}
                                    />
                                </g>
                                {/* Overlay invisible sur la poignée de rotation (hitbox élargie) */}
                                {protRotHandlePos && (
                                    <circle
                                        cx={protRotHandlePos.x} cy={protRotHandlePos.y} r={26}
                                        fill="transparent"
                                        style={{ cursor: 'grab' }}
                                        onPointerDown={onProtRotateHandlePointerDown}
                                    />
                                )}
                                {/* Feedback de visée : trait fantôme + degré courant (glisser sur l'arc) */}
                                {protGesture === 'aim' && aimAngle != null && (() => {
                                    // `aimAngle` est la MESURE ; la direction visée est la
                                    // graduation, qui en diffère quand le rapporteur est retourné.
                                    const dirVisee = protRotation + (protInverse ? 180 - aimAngle : aimAngle);
                                    const end = polarPoint(protPos.x, protPos.y, PROT_RADIUS + 6, dirVisee);
                                    const lbl = polarPoint(protPos.x, protPos.y, PROT_RADIUS + 30, dirVisee);
                                    return (
                                        <g pointerEvents="none">
                                            <line x1={protPos.x} y1={protPos.y} x2={end.x} y2={end.y} stroke="#2563eb" strokeWidth={2} strokeDasharray="5 3" />
                                            <circle cx={end.x} cy={end.y} r={4} fill="#2563eb" />
                                            <text
                                                x={lbl.x} y={lbl.y} textAnchor="middle" dominantBaseline="central"
                                                fontSize={18} fontWeight={800} fill="#2563eb"
                                                stroke="white" strokeWidth={3.5} paintOrder="stroke" strokeLinejoin="round"
                                                fontFamily="system-ui,sans-serif"
                                            >{aimAngle}°</text>
                                        </g>
                                    );
                                })()}
                            </g>
                        );
                    })()}

                    {/* Aide visuelle pour task active kind='line' ou 'segment' : halos sur
                        les 2 points attendus + disque coloré sur le 1er clic éventuel */}
                    {activeTask
                        && (activeTask.kind === 'line' || activeTask.kind === 'segment')
                        && (expected[activeTask.id]?.type === 'line' || expected[activeTask.id]?.type === 'segment')
                        && (() => {
                        const exp = expected[activeTask.id];
                        const remaining = [exp.p1, exp.p2].filter(p => {
                            if (!firstClick) return true;
                            return !(Math.abs(p.x - firstClick.x) < 0.1 && Math.abs(p.y - firstClick.y) < 0.1);
                        });
                        return (
                            <g>
                                {remaining.map((p, i) => (
                                    <circle
                                        key={`target-halo-${i}`}
                                        cx={p.x} cy={p.y} r={11}
                                        fill={activeTask.color} opacity="0.25"
                                    />
                                ))}
                                {firstClick && (
                                    <circle
                                        cx={firstClick.x} cy={firstClick.y} r={4.5}
                                        fill={activeTask.color} stroke="white" strokeWidth="1.5"
                                    />
                                )}
                            </g>
                        );
                    })()}

                    {/* Prévisualisation en pointillés pendant le drag */}
                    {phase === 'dragging' && draft && activeTask && (
                        <g>
                            <circle
                                cx={draft.cx} cy={draft.cy} r={Math.max(0, draft.r)}
                                fill="none" stroke={activeTask.color} strokeWidth="2"
                                strokeDasharray="6 4"
                            />
                            <circle cx={draft.cx} cy={draft.cy} r={3} fill={activeTask.color} />
                            {draft.r > 3 && (
                                <text
                                    x={draft.cx + draft.r + 6}
                                    y={draft.cy - 4}
                                    fontSize="13"
                                    fontWeight="700"
                                    fill="#2c2c2a"
                                    stroke="white"
                                    strokeWidth="3"
                                    paintOrder="stroke"
                                    strokeLinejoin="round"
                                    fontFamily="system-ui,sans-serif"
                                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                                >
                                    {(draft.r / pxPerCm).toFixed(1)} {unite}
                                </text>
                            )}
                        </g>
                    )}

                    {/* Points nommés */}
                    {points.map((p, i) => {
                        const isTarget = activeTask && phase === 'idle' && (() => {
                            const exp = expected[activeTask.id];
                            return exp && Math.hypot(p.x - exp.cx, p.y - exp.cy) < 2;
                        })();
                        return (
                            <g key={`point-${i}`}>
                                <circle
                                    cx={p.x} cy={p.y} r={isTarget ? 6 : 4}
                                    fill="#2c2c2a"
                                    className={isTarget ? 'animate-pulse' : ''}
                                />
                                <text
                                    x={p.x + 9} y={p.y - 5}
                                    fontSize="14" fontWeight="500"
                                    fill="#2c2c2a"
                                    fontFamily="system-ui,sans-serif"
                                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                                >
                                    {p.name}
                                </text>
                            </g>
                        );
                    })}

                    {/* Toast feedback */}
                    {feedback && (
                        <g>
                            <rect x={W / 2 - 120} y={H - 50} width={240} height={34} rx={8}
                                fill={feedback.type === 'success' ? '#1D9E75' : '#E24B4A'}
                                opacity="0.95"
                            />
                            <text
                                x={W / 2} y={H - 28}
                                textAnchor="middle"
                                fontSize="14"
                                fill="white"
                                fontFamily="system-ui,sans-serif"
                                fontWeight="600"
                            >
                                {feedback.msg}
                            </text>
                        </g>
                    )}
                </svg>
            </div>
    );

    const content = (
        <div className={isExpanded ? 'h-full flex flex-col gap-2' : 'space-y-3'}>
            {/* Progression */}
            <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className={isExpanded ? 'text-slate-300' : (clair ? 'text-slate-600' : 'text-slate-400')}>
                    📐 Progression : <strong>{nSolved}</strong> / {nTotal} tracé{nSolved > 1 ? 's' : ''}
                    {nSkipped > 0 ? ` (${nSkipped} passé${nSkipped > 1 ? 's' : ''})` : ''}
                </span>
                <div className="flex items-center gap-2">
                    {!isExpanded && !corrige && (
                        <button
                            type="button"
                            onClick={() => setIsExpanded(true)}
                            className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border transition ${clair ? 'text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100' : 'text-blue-200 border-blue-500/40 bg-blue-500/10 hover:bg-blue-500/20'}`}
                        >
                            <Maximize2 size={13} /> Agrandir
                        </button>
                    )}
                    {canSkip && !corrige && (
                        <button
                            onClick={handleSkipTask}
                            className={`px-2.5 py-1 text-xs rounded-md border transition ${clair ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200' : 'bg-slate-700/60 text-slate-200 border-slate-600 hover:bg-slate-600'}`}
                        >
                            ⏭ Passer cette tâche
                        </button>
                    )}
                    {/* Abandon : uniquement en passation (onAbandon fourni par Quiz),
                        pas dans l'aperçu exerciseur */}
                    {onAbandon && !confirmAbandon && !corrige && (
                        <button
                            onClick={() => setConfirmAbandon(true)}
                            className={`px-2.5 py-1 text-xs rounded-md border transition ${clair ? 'text-slate-500 border-slate-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300' : 'text-slate-400 border-slate-600 hover:bg-red-900/30 hover:text-red-300 hover:border-red-700/60'}`}
                        >
                            🏳 Abandonner
                        </button>
                    )}
                </div>
            </div>

            {/* Confirmation d'abandon — inline, pas de window.confirm */}
            {onAbandon && confirmAbandon && (
                <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs ${clair ? 'bg-red-50 border-red-300' : 'bg-red-900/20 border-red-700/50'}`}>
                    <span className={clair ? 'text-red-700' : 'text-red-200'}>
                        Abandonner ? Ta construction sera effacée et la question comptée fausse.
                        Tu pourras y revenir avec « Retour ».
                    </span>
                    <div className="flex gap-1.5 flex-shrink-0">
                        <button
                            onClick={handleAbandon}
                            className="px-2.5 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold transition"
                        >
                            Oui, abandonner
                        </button>
                        <button
                            onClick={() => setConfirmAbandon(false)}
                            className={`px-2.5 py-1 rounded-md border transition ${clair ? 'text-slate-600 border-slate-300 hover:bg-slate-100' : 'text-slate-300 border-slate-600 hover:bg-slate-700'}`}
                        >
                            Continuer
                        </button>
                    </div>
                </div>
            )}

            {/* Consignes : liste complète (inline) ou consigne unique (plein écran) */}
            {!isExpanded ? (
            <div className="flex flex-col gap-1.5">
                {tasks.map((t) => {
                    const solved = !!validatedShapes[t.id];
                    const skipped = skippedTasks.has(t.id);
                    const isActive = activeTaskId === t.id;
                    const hasError = feedback?.type === 'error' && feedback?.taskId === t.id;
                    // En correction : le verdict de la consigne, tracée ou non
                    const e = etats ? etats.get(t.id) : null;
                    const etat = e ? (e.juste ? 'juste' : 'faux') : null;

                    return (
                        <button
                            key={t.id}
                            onClick={() => handleTaskClick(t.id)}
                            disabled={solved || skipped || Boolean(etat)}
                            data-etat={etat || undefined}
                            className={`text-left text-sm px-3 py-2 rounded-lg border transition flex items-center gap-2 ${
                                etat
                                    ? `border-2 ${th[etat]} cursor-default`
                                    : isActive
                                    ? (clair ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-blue-400 bg-slate-700/50 text-white')
                                    : solved
                                        ? (clair ? 'border-emerald-300 bg-emerald-50 text-emerald-700 opacity-90 cursor-default' : 'border-emerald-600/60 bg-emerald-900/20 text-slate-300 opacity-75 cursor-default')
                                        : skipped
                                            ? (clair ? 'border-slate-300 bg-slate-100 text-slate-400 opacity-70 cursor-default' : 'border-slate-600 bg-slate-800/40 text-slate-400 opacity-60 cursor-default')
                                            : hasError
                                                ? (clair ? 'border-red-400 bg-red-50 text-slate-800' : 'border-red-500 bg-slate-700/30 text-slate-200')
                                                : (clair ? 'border-slate-200 bg-white text-slate-800 hover:border-blue-300' : 'border-slate-600 bg-slate-800/50 text-slate-200 hover:border-slate-500')
                            }`}
                        >
                            <span
                                className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                style={{ background: t.color }}
                            />
                            <HtmlKatex html={t.consigne} className="flex-1" />
                            <span className={`text-xs ml-auto ${etat ? 'font-black' : ''}`}>
                                {etat ? marqueSigne(etat) : solved ? '✓' : skipped ? '⏭' : hasError ? '✗' : ''}
                            </span>
                        </button>
                    );
                })}
            </div>
            ) : (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700 bg-slate-800/60">
                    {activeTask ? (
                        <>
                            <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: activeTask.color }} />
                            <HtmlKatex html={activeTask.consigne} className="flex-1 text-sm text-slate-100" />
                            <span className="text-xs text-slate-400 ml-auto flex-shrink-0">Consigne {tasks.findIndex((t) => t.id === activeTaskId) + 1}/{nTotal}</span>
                        </>
                    ) : (
                        <span className="flex-1 text-sm font-semibold text-emerald-400 text-center">Terminé ✅ Toutes les consignes sont tracées.</span>
                    )}
                </div>
            )}

            {/* Instruction + unité de la figure (le carreau EST l'unité de travail) */}
            <p className={`text-xs sm:text-sm italic min-h-[20px] ${isExpanded ? 'text-slate-300' : (clair ? 'text-slate-500' : 'text-slate-400')}`}>{corrige ? 'Ta figure à gauche, la figure attendue à droite.' : instruction}</p>
            <p className={`text-xs font-medium ${isExpanded ? 'text-slate-300' : (clair ? 'text-slate-600' : 'text-slate-300')}`}>
                Sur la figure : 1 carreau = 1 {unite}.
            </p>

            {/* Canvas SVG, et en correction la figure attendue à côté */}
            {corrige ? (
                <div className="grid gap-3 sm:grid-cols-2 sm:items-start" data-testid="figures-cote-a-cote">
                    {canvas}
                    <div>
                        <span className="block mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">La réponse</span>
                        <div className="w-full rounded-xl border-2 border-dashed border-emerald-500 bg-white">
                            <ProgrammeConstructionRenderer
                                viewBox={viewBox} points={points} preShapes={preShapes} tasks={tasks} pxPerCm={pxPerCm}
                                bonneReponse={corrige.bonneReponse} showConsignes={false} light
                            />
                        </div>
                    </div>
                </div>
            ) : canvas}

            {isExpanded && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition shadow-lg shadow-blue-500/30"
                >
                    Terminé
                </button>
            )}
        </div>
    );

    return isExpanded ? (
        <PleinEcranInteractif open onClose={() => setIsExpanded(false)} title="Programme de construction">
            {content}
        </PleinEcranInteractif>
    ) : content;
});

export default ProgrammeConstructionEleve;
